import { assetPackageLocator, assetReferenceKey, createAssetReference } from '../../plugin-contracts/asset_reference_contract.mjs';

const MANIFEST_LIMIT = 3 * 1024 * 1024;
const LEGACY_VIEWS = ['top', 'bottom', 'front', 'back', 'left', 'right'];
const ARTIFACT_LIMIT = 256 * 1024 * 1024;
const check = (ok, message) => { if (!ok) throw new TypeError(`Asset package: ${message}`); };
const freeze = value => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze); Object.freeze(value);
  }
  return value;
};
const sha256 = async bytes => [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
  .map(value => value.toString(16).padStart(2, '0')).join('');

// Only used on bounded, parsed HTTP JSON. Sorted UTF-16 keys and ECMAScript
// number/string serialization match the writer's RFC 8785 package digests.
function canonical(value, depth = 0, budget = { nodes: 0 }) {
  check(depth <= 64 && ++budget.nodes <= 250000, 'manifest structure limit');
  if (value === null || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'number') { check(Number.isFinite(value), 'nonfinite number'); return JSON.stringify(value); }
  if (typeof value === 'string') { check(value.isWellFormed(), 'invalid Unicode'); return JSON.stringify(value); }
  if (Array.isArray(value)) return `[${value.map(child => canonical(child, depth + 1, budget)).join(',')}]`;
  check(value && typeof value === 'object', 'JSON value required');
  return `{${Object.keys(value).sort().map(key => {
    check(key.isWellFormed(), 'invalid Unicode key');
    return `${JSON.stringify(key)}:${canonical(value[key], depth + 1, budget)}`;
  }).join(',')}}`;
}

async function readBytes(response, limit, signal, exact = false) {
  check(response.body?.getReader, 'streaming response required');
  const reader = response.body.getReader(), chunks = [];
  let length = 0;
  const abort = () => { Promise.resolve(reader.cancel()).catch(() => undefined); };
  signal.addEventListener('abort', abort, { once: true });
  try {
    while (true) {
      check(!signal.aborted, 'aborted');
      const { value, done } = await reader.read();
      check(!signal.aborted, 'aborted');
      if (done) break;
      length += value.byteLength;
      check(length <= limit, 'response byte limit'); chunks.push(value);
    }
    check(!exact || length === limit, 'artifact length mismatch');
    const bytes = new Uint8Array(length); let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    return bytes;
  } catch (error) { try { await reader.cancel(); } catch { /* Keep original error. */ } throw error; }
  finally { signal.removeEventListener('abort', abort); reader.releaseLock(); }
}
const baseUrl = locator => `/api/asset-objects/${locator.assetObjectId}/revisions/${locator.revisionId}/variants/${locator.variantId}`;

/** Activation-owned, read-only resolver. It never asks for a current revision. */
export function createAssetPackageReader({ fetchImpl = fetch, digest = sha256, authorize = () => { /* Standalone reader; host supplies its owner check. */ }, getContext = () => null } = {}) {
  const pending = new Set(), packages = new Map();
  let disposed = false, generation = 0, cachedBytes = 0;
  async function run(operation) {
    if (disposed) return Object.freeze({ status: 'disposed' });
    const controller = new AbortController(), captured = generation, context = getContext();
    pending.add(controller);
    const stale = () => disposed || captured !== generation || context !== getContext();
    try {
      const result = await operation(controller.signal);
      return stale() ? Object.freeze({ status: disposed ? 'disposed' : 'cancelled' }) : result;
    } catch (error) {
      if (stale()) return Object.freeze({ status: disposed ? 'disposed' : 'cancelled' });
      return Object.freeze({ status: error instanceof TypeError && error.message.startsWith('Asset package:')
        ? 'invalid-package' : 'read-error' });
    } finally { pending.delete(controller); }
  }
  async function response(path, signal) {
    check(!signal.aborted, 'aborted');
    // Immutable revision URLs may reuse private HTTP bodies after server-side
    // revalidation. The byte lengths, hashes and bindings below still apply.
    const result = await fetchImpl(path, { signal, cache: 'no-cache', credentials: 'same-origin' });
    if (result.status === 404) return null;
    if (!result.ok) throw new Error(`HTTP ${result.status}`);
    return result;
  }
  async function load(locator, expected, signal) {
    const cacheKey = expected ? assetReferenceKey(expected) : null;
    if (cacheKey && packages.has(cacheKey)) return packages.get(cacheKey).value;
    const res = await response(`${baseUrl(locator)}/package`, signal);
    if (!res) return Object.freeze({ status: 'missing-package' });
    const descriptorBytes = await readBytes(res, MANIFEST_LIMIT, signal);
    let payload;
    try { payload = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(descriptorBytes)); }
    catch (error) { if (signal.aborted) throw error; check(false, 'invalid descriptor JSON'); }
    const { reference: raw, definition, variant } = payload || {};
    let reference;
    try { reference = createAssetReference(raw); } catch { check(false, 'invalid reference'); }
    check(['assetObjectId', 'revisionId', 'variantId'].every(key => reference[key] === locator[key]), 'locator mismatch');
    check(!expected || assetReferenceKey(reference) === cacheKey, 'persisted reference mismatch');
    check(definition?.contract === 'ks.asset-geometry' && definition.contractVersion === 1
      && variant?.contract === 'ks.asset-geometry-variant' && variant.contractVersion === 1, 'manifest contract');
    check(definition.assetId === reference.assetId && definition.revisionId === reference.revisionId
      && ['assetId', 'revisionId', 'variantId'].every(key => variant.identity?.[key] === reference[key])
      && definition.packageDigest === reference.packageDigest && variant.definitionDigest === reference.packageDigest
      && variant.variantDigest === reference.variantDigest && definition.sourceSTEP?.sha256 === reference.sourceSha256, 'manifest binding');
    const { packageDigest: _package, ...unsignedDefinition } = definition;
    const { variantDigest: _variant, ...unsignedVariant } = variant;
    check(await digest(new TextEncoder().encode(canonical(unsignedDefinition))) === reference.packageDigest
      && await digest(new TextEncoder().encode(canonical(unsignedVariant))) === reference.variantDigest, 'manifest digest');
    const value = freeze({ status: 'ready', reference, definition, variant });
    if (!signal.aborted && !disposed) {
      // Immutable manifests only; never retain large model/feature bytes here.
      const storedKey = assetReferenceKey(reference);
      cachedBytes -= packages.get(storedKey)?.bytes || 0;
      packages.delete(storedKey);
      // Conservative serialized-size reservation; no claim about native heap size.
      const bytes = descriptorBytes.byteLength * 4;
      while (packages.size && (packages.size >= 32 || cachedBytes + bytes > 16 * 1024 * 1024)) {
        const oldest = packages.keys().next().value;
        cachedBytes -= packages.get(oldest).bytes; packages.delete(oldest);
      }
      packages.set(storedKey, { value, bytes }); cachedBytes += bytes;
    }
    return value;
  }
  function locate(value, pinned) {
    const reference = pinned ? createAssetReference(value) : null;
    const locator = assetPackageLocator(reference || value);
    authorize(locator.assetObjectId, reference, locator);
    return { locator, reference };
  }
  function reset() { generation++; for (const controller of pending) controller.abort(); packages.clear(); cachedBytes = 0; }
  return Object.freeze({
    resolve(value) { const { locator } = locate(value, false); return run(signal => load(locator, null, signal)); },
    open(value) { const { locator, reference } = locate(value, true); return run(signal => load(locator, reference, signal)); },
    readArtifact(value, kind, viewId) {
      const { locator, reference } = locate(value, true);
      check(['source', 'recipe', 'compatibilityRecipe', 'cad', 'glb', 'features', 'projection', 'legacyGlb', 'legacySvg', 'legacyView', 'legacyPlan'].includes(kind), 'unsupported artifact');
      check(kind === 'projection' ? typeof viewId === 'string' && /^[a-z][a-z0-9-]{0,63}$/.test(viewId)
        : ['legacyView', 'legacyPlan'].includes(kind) ? LEGACY_VIEWS.includes(viewId) : viewId === undefined, 'invalid view selector');
      return run(async signal => {
        const pkg = await load(locator, reference, signal);
        if (pkg.status !== 'ready') return pkg;
        const outputs = pkg.variant.outputs;
        // legacyPlan: monochrome GPU plan document, only in revisions written since U08c Block 5.
        const legacyPath = { legacyGlb: 'legacy/model.glb', legacySvg: 'legacy/top_view.svg', legacyView: `legacy/view_${viewId}.svg`,
          legacyPlan: `legacy/plan_${viewId}.projection.json` }[kind];
        const ref = legacyPath ? outputs.compatibility?.files.find(file => file.path === legacyPath) : kind === 'source' ? pkg.definition.sourceSTEP : kind === 'recipe' ? pkg.definition.recipe?.artifact
          : kind === 'compatibilityRecipe' ? outputs.compatibility?.recipe?.artifact : kind === 'cad' ? outputs.normalizedCAD
            : kind === 'glb' ? outputs.glb?.artifact : kind === 'features' ? outputs.featureDocument?.artifact
              : outputs.views?.find(view => view.viewId === viewId)?.projection?.artifact;
        if (!ref) return Object.freeze({ status: 'missing-artifact' });
        check(Number.isSafeInteger(ref.byteLength) && ref.byteLength > 0 && ref.byteLength <= ARTIFACT_LIMIT
          && typeof ref.sha256 === 'string' && /^[a-f0-9]{64}$/.test(ref.sha256), 'artifact declaration');
        const res = await response(`${baseUrl(locator)}/${kind}${viewId !== undefined ? `?view=${encodeURIComponent(viewId)}` : ''}`, signal);
        if (!res) return Object.freeze({ status: 'missing-artifact' });
        const bytes = await readBytes(res, ref.byteLength, signal, true);
        check(await digest(bytes) === ref.sha256, 'artifact digest');
        return Object.freeze({ status: 'ready', reference, artifact: ref, bytes });
      });
    },
    invalidate: reset,
    dispose() { disposed = true; reset(); }
  });
}
